import { createContext, ReactNode, createElement, useCallback, useContext, useEffect, useState } from 'react';
import { apiService, InvestmentRecord, BuyInvestmentRequest, SellInvestmentRequest, UpdateInvestmentRequest } from '../services/api';
import { useAuth } from './useAuth';
import { localStorageUtils } from '../utils/localStorage';
import { getApiFailureMessage, getUnifiedApiErrorMessage } from '../utils/apiErrorMessage';

// React StrictMode(개발)에서 마운트/언마운트가 한 번 더 발생해 동일 네트워크 요청이 2번 찍힐 수 있다.
// "초기 로드(fetchInvestments())"만은 모듈 전역 캐시/인플라이트로 중복 네트워크를 막는다.
type InvestmentsCache = {
  investments: InvestmentRecord[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
};

let defaultInvestmentsInFlight: Promise<InvestmentsCache> | null = null;
let defaultInvestmentsCached: InvestmentsCache | null = null;
let defaultInvestmentsCachedAt = 0;
const DEFAULT_INVESTMENTS_TTL_MS = 1500;

export interface InvestmentState {
  investments: InvestmentRecord[];
  isLoading: boolean;
  error: string | null;
  totalElements: number;
  totalPages: number;
  currentPage: number;
}

export interface InvestmentActions {
  fetchInvestments: (params?: {
    page?: number;
    size?: number;
    type?: string;
    startDate?: string;
    endDate?: string;
  }) => Promise<void>;
  createBuyInvestment: (data: BuyInvestmentRequest) => Promise<boolean>;
  createSellInvestment: (data: SellInvestmentRequest) => Promise<boolean>;
  updateInvestment: (data: UpdateInvestmentRequest) => Promise<boolean>;
  deleteInvestment: (id: number) => Promise<boolean>;
  clearError: () => void;
}

type InvestmentsContextValue = InvestmentState & InvestmentActions;

const InvestmentsContext = createContext<InvestmentsContextValue | undefined>(undefined);

export const InvestmentsProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const [investments, setInvestments] = useState<InvestmentRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  // 투자 기록 목록 조회
  const fetchInvestments = useCallback(async (params: {
    page?: number;
    size?: number;
    type?: string;
    startDate?: string;
    endDate?: string;
  } = {}) => {
    try {
      setIsLoading(true);
      setError(null);

      if (isAuthenticated) {
        const isDefaultRequest = Object.keys(params).length === 0;

        if (isDefaultRequest) {
          const now = Date.now();
          if (defaultInvestmentsCached && now - defaultInvestmentsCachedAt < DEFAULT_INVESTMENTS_TTL_MS) {
            const cached = defaultInvestmentsCached;
            setInvestments(cached.investments);
            setTotalElements(cached.totalElements);
            setTotalPages(cached.totalPages);
            setCurrentPage(cached.currentPage);
            setIsLoading(false);
            return;
          }

          if (defaultInvestmentsInFlight) {
            const cached = await defaultInvestmentsInFlight;
            setInvestments(cached.investments);
            setTotalElements(cached.totalElements);
            setTotalPages(cached.totalPages);
            setCurrentPage(cached.currentPage);
            setIsLoading(false);
            return;
          }

          defaultInvestmentsInFlight = (async () => {
            // 모든 기록을 가져오기 위해 여러 페이지를 순회
            let allInvestments: InvestmentRecord[] = [];
            let currentPage = params.page || 0;
            const pageSize = params.size || 1000;
            let hasMore = true;

            while (hasMore) {
              const response = await apiService.getInvestments({
                ...params,
                page: currentPage,
                size: pageSize
              });

              if (response.success && response.data) {
                allInvestments = [...allInvestments, ...response.data.content];

                // 마지막 페이지인지 확인
                if (currentPage >= response.data.totalPages - 1 || response.data.content.length < pageSize) {
                  hasMore = false;
                } else {
                  currentPage++;
                }
              } else {
                hasMore = false;
                throw new Error(getUnifiedApiErrorMessage(response));
              }
            }

            return {
              investments: allInvestments,
              totalElements: allInvestments.length,
              totalPages: Math.ceil(allInvestments.length / (params.size || 1000)),
              currentPage: 0,
            } satisfies InvestmentsCache;
          })();

          try {
            const computedCache = await defaultInvestmentsInFlight;
            defaultInvestmentsCached = computedCache;
            defaultInvestmentsCachedAt = Date.now();
            defaultInvestmentsInFlight = null;

            setInvestments(computedCache.investments);
            setTotalElements(computedCache.totalElements);
            setTotalPages(computedCache.totalPages);
            setCurrentPage(computedCache.currentPage);
            setIsLoading(false);
            return;
          } catch (e) {
            defaultInvestmentsInFlight = null;
            throw e;
          }
        }

        // 로그인한 경우(기본 요청이 아닌 경우): DB에서만 조회 (로컬스토리지 데이터는 표시하지 않음)
        // 모든 기록을 가져오기 위해 여러 페이지를 순회
        let allInvestments: InvestmentRecord[] = [];
        let currentPage = params.page || 0;
        const pageSize = params.size || 1000;
        let hasMore = true;

        while (hasMore) {
          const response = await apiService.getInvestments({
            ...params,
            page: currentPage,
            size: pageSize
          });

          if (response.success && response.data) {
            allInvestments = [...allInvestments, ...response.data.content];

            // 마지막 페이지인지 확인
            if (currentPage >= response.data.totalPages - 1 || response.data.content.length < pageSize) {
              hasMore = false;
            } else {
              currentPage++;
            }
          } else {
            hasMore = false;
            setError(getUnifiedApiErrorMessage(response));
          }
        }

        if (allInvestments.length > 0 || !hasMore) {
          setInvestments(allInvestments);
          setTotalElements(allInvestments.length);
          setTotalPages(Math.ceil(allInvestments.length / pageSize));
          setCurrentPage(0);
        }
      } else {
        // 로그인하지 않은 경우: 로컬스토리지에서 조회
        const localInvestments = localStorageUtils.getInvestments();
        
        // 필터링 적용
        let filtered = [...localInvestments];
        if (params.type) {
          filtered = filtered.filter(inv => inv.type === params.type);
        }
        if (params.startDate) {
          const startDate = params.startDate;
          filtered = filtered.filter(inv => inv.date >= startDate);
        }
        if (params.endDate) {
          const endDate = params.endDate;
          filtered = filtered.filter(inv => inv.date <= endDate);
        }
        
        // 날짜순 정렬
        filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        
        setInvestments(filtered);
        setTotalElements(filtered.length);
        setTotalPages(1);
        setCurrentPage(0);
      }
    } catch (error: any) {
      console.error('투자 기록 조회 오류:', error);
      setError(getApiFailureMessage(error, '투자 기록을 불러오는 중 오류가 발생했습니다.'));
      defaultInvestmentsInFlight = null;
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // 매수 기록 생성
  const createBuyInvestment = useCallback(async (data: BuyInvestmentRequest): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      if (isAuthenticated) {
        // 로그인한 경우: DB에만 저장 (로컬스토리지에 저장하지 않음)
        const response = await apiService.createBuyInvestment(data);
        
        if (response.success) {
          await fetchInvestments();
          return true;
        } else {
          setError(getUnifiedApiErrorMessage(response));
          return false;
        }
      } else {
        // 로그인하지 않은 경우: 로컬스토리지에 저장
        const localInvestments = localStorageUtils.getInvestments();
        const newRecord = {
          ...data,
          id: Date.now(), // 임시 ID
          date: data.recordDate, // recordDate를 date로 변환
          type: 'BUY',
          createdAt: new Date().toISOString()
        };
        localInvestments.push(newRecord);
        localStorageUtils.setInvestments(localInvestments);
        await fetchInvestments();
        return true;
      }
    } catch (error: any) {
      console.error('매수 기록 생성 오류:', error);
      setError(getApiFailureMessage(error, '매수 기록 생성 중 오류가 발생했습니다.'));
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [fetchInvestments, isAuthenticated]);

  // 매도 기록 생성
  const createSellInvestment = useCallback(async (data: SellInvestmentRequest): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      if (isAuthenticated) {
        // 로그인한 경우: DB에만 저장 (로컬스토리지에 저장하지 않음)
        const response = await apiService.createSellInvestment(data);
        
        if (response.success) {
          await fetchInvestments();
          return true;
        } else {
          setError(getUnifiedApiErrorMessage(response));
          return false;
        }
      } else {
        // 로그인하지 않은 경우: 로컬스토리지에 저장
        const localInvestments = localStorageUtils.getInvestments();
        const newRecord = {
          ...data,
          id: Date.now(), // 임시 ID
          date: data.recordDate, // recordDate를 date로 변환
          type: 'SELL',
          createdAt: new Date().toISOString()
        };
        localInvestments.push(newRecord);
        localStorageUtils.setInvestments(localInvestments);
        await fetchInvestments();
        return true;
      }
    } catch (error: any) {
      console.error('매도 기록 생성 오류:', error);
      setError(getApiFailureMessage(error, '매도 기록 생성 중 오류가 발생했습니다.'));
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [fetchInvestments, isAuthenticated]);

  // 투자 기록 수정
  const updateInvestment = useCallback(async (data: UpdateInvestmentRequest): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      if (isAuthenticated) {
        // 로그인한 경우: DB에서만 수정 (로컬스토리지에 저장하지 않음)
        const response = await apiService.updateInvestment(data);
        
        if (response.success) {
          await fetchInvestments();
          return true;
        } else {
          setError(getUnifiedApiErrorMessage(response));
          return false;
        }
      } else {
        // 로그인하지 않은 경우: 로컬스토리지에서 수정
        const localInvestments = localStorageUtils.getInvestments();
        const index = localInvestments.findIndex(inv => inv.id === data.id);
        if (index !== -1) {
          localInvestments[index] = { ...localInvestments[index], ...data };
          localStorageUtils.setInvestments(localInvestments);
          await fetchInvestments();
          return true;
        } else {
          setError('수정할 투자 기록을 찾을 수 없습니다.');
          return false;
        }
      }
    } catch (error: any) {
      console.error('투자 기록 수정 오류:', error);
      setError(getApiFailureMessage(error, '투자 기록 수정 중 오류가 발생했습니다.'));
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [fetchInvestments, isAuthenticated]);

  // 투자 기록 삭제
  const deleteInvestment = useCallback(async (id: number): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      if (isAuthenticated) {
        // 로그인한 경우: DB에서만 삭제 (로컬스토리지와 무관)
        const response = await apiService.deleteInvestment(id);
        
        if (response.success) {
          await fetchInvestments();
          return true;
        } else {
          setError(getUnifiedApiErrorMessage(response));
          return false;
        }
      } else {
        // 로그인하지 않은 경우: 로컬스토리지에서 삭제
        const localInvestments = localStorageUtils.getInvestments();
        const filtered = localInvestments.filter(inv => inv.id !== id);
        localStorageUtils.setInvestments(filtered);
        await fetchInvestments();
        return true;
      }
    } catch (error: any) {
      console.error('투자 기록 삭제 오류:', error);
      setError(getApiFailureMessage(error, '투자 기록 삭제 중 오류가 발생했습니다.'));
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [fetchInvestments, isAuthenticated]);

  // 에러 초기화
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // App 트리에서 Provider가 마운트되는 1곳에서만 초기 로드 수행
  useEffect(() => {
    if (isAuthenticated) {
      fetchInvestments();
    } else {
      const localInvestments = localStorageUtils.getInvestments();
      setInvestments(localInvestments);
      setTotalElements(localInvestments.length);
      setTotalPages(1);
      setCurrentPage(0);
      setError(null);
      setIsLoading(false);
    }
  }, [fetchInvestments, isAuthenticated]);

  const value: InvestmentsContextValue = {
    investments,
    isLoading,
    error,
    totalElements,
    totalPages,
    currentPage,
    fetchInvestments,
    createBuyInvestment,
    createSellInvestment,
    updateInvestment,
    deleteInvestment,
    clearError,
  };

  return createElement(InvestmentsContext.Provider, { value }, children);
};

export const useInvestments = (): InvestmentsContextValue => {
  const ctx = useContext(InvestmentsContext);
  if (!ctx) {
    throw new Error('useInvestments는 InvestmentsProvider 내부에서 사용되어야 합니다.');
  }
  return ctx;
};
